/**
 * AppRouter.ts
 * Master Path-Based Router & Auth Protection Engine.
 * 
 * Rules:
 * 1. Public Routes (/, /home, /course/:id, /courses (guest), /tools/:id, /privacy-policy, /terms-conditions, etc.)
 *    - Redirect immediately to Angular fa-frontend root or routes.
 *    - Digital Certificate verification (/certificate/:id) remains active in ASAE via CertificateView.
 * 2. Protected Routes (/lms-dashboard, /studio, /learn, /courses, /my-courses, /create-course, /exam-generator, /assignment-generator, /my-exams, /my-assignments, /assignment-checker, /exam-solve, /assignment-solve)
 *    - STRICTLY PROTECTED: Unauthenticated guests are shown 401 Unauthorized page with Login, Sign Up & Enroll actions.
 */
import { AuthService } from '../services/AuthService';
import { AppShell } from '../layout/AppShell';
import { applyAuthDirectives, initAuthDirectivesObserver } from '../directives/AuthDirectives';

/**
 * Helper to get the correct Angular frontend portal URL:
 * - In local development (port 5173), points to Angular dev server on port 4200.
 * - In production (port 80/443), points to the same origin relative path.
 */
export function getAngularPortalUrl(path: string = ''): string {
  const normalizedPath = path.startsWith('/') ? path : '/' + path;
  if (typeof window !== 'undefined' && window.location.port === '5173') {
    return `http://${window.location.hostname}:4200${normalizedPath}`;
  }
  return normalizedPath;
}

export class AppRouter {
  private container: HTMLElement;
  private currentComponent: any = null;

  constructor(container: HTMLElement) {
    this.container = container;
    window.addEventListener('popstate', () => this.handleRoute());
    initAuthDirectivesObserver(document.body);
  }

  public navigate(path: string): void {
    window.history.pushState({}, '', path);
    this.handleRoute();
  }

  public handleRoute(): void {
    if (this.currentComponent && typeof this.currentComponent.destroy === 'function') {
      this.currentComponent.destroy();
      this.currentComponent = null;
    }

    const fullPath = window.location.pathname;
    const appEl = document.getElementById('app');
    if (appEl && !fullPath.startsWith('/studio')) {
      appEl.style.height = '';
      appEl.style.maxHeight = '';
      appEl.style.overflow = '';
    }

    const auth = AuthService.getInstance();
    const searchParams = new URLSearchParams(window.location.search);
    const courseId = searchParams.get('courseId') || (fullPath.startsWith('/learn/') ? fullPath.split('/learn/')[1]?.split('?')[0] : null) || 'course-1st-evs';

    // 1. ROOT & PUBLIC REDIRECTIONS
    if (fullPath === '/' || fullPath === '/home') {
      // In standalone ASAE (e.g. port 5173), route to the LMS dashboard internally
      if (window.location.port === '5173') {
        this.navigate('/lms-dashboard');
        return;
      }
      // In unified production, redirect to Angular portal root only if not already on it
      const target = getAngularPortalUrl('/');
      if (window.location.href !== target && window.location.pathname !== '/') {
        window.location.href = target;
      } else {
        this.navigate('/lms-dashboard');
      }
      return;
    }

    const publicPortalPaths = [
      '/privacy-policy', '/terms-conditions', '/refund-policy', '/pricing', '/contact-us', '/about-us',
      '/apna-exam-guru', '/ai-exam-guru',
      '/apna-ai-interviewer',
      '/apna-assignment-guru', '/apna-assessment-guru', '/assessment-builder',
      '/apna-personal-guru', '/apna-personal-tutor',
      '/ai-course-generator-studio', '/ai-story-platform',
      '/w/'
    ];
    if (publicPortalPaths.some(p => fullPath.startsWith(p))) {
      const target = getAngularPortalUrl(fullPath);
      if (window.location.href !== target) {
        window.location.href = target;
      }
      return;
    }

    if (fullPath.startsWith('/course/')) {
      const cId = fullPath.split('/course/')[1]?.split('/')[0] || searchParams.get('id');
      const targetPath = cId ? `/course/details/${cId}` : '/course';
      window.location.href = getAngularPortalUrl(targetPath);
      return;
    }

    if (fullPath.startsWith('/tools/')) {
      window.location.href = getAngularPortalUrl('/');
      return;
    }

    if ((fullPath === '/courses' || fullPath.startsWith('/courses')) && !auth.isAuthenticated()) {
      window.location.href = getAngularPortalUrl('/course');
      return;
    }

    if (fullPath.startsWith('/certificate/')) {
      const certId = fullPath.split('/certificate/')[1]?.split('/')[0] || searchParams.get('id') || '2026-DEMO';
      import('../public/CertificateView').then(({ CertificateView }) => {
        const view = new CertificateView(this.container, certId);
        view.render();
        this.currentComponent = view;
      });
      return;
    }

    // 2. AUTHENTICATION ROUTE (/login hands off to fa-frontend /auth/login)
    if (fullPath.startsWith('/login')) {
      if (auth.isAuthenticated()) {
        this.navigate('/lms-dashboard');
        return;
      }
      window.location.href = getAngularPortalUrl('/auth/login');
      return;
    }

    // 3. STUDENT LEARNER VIEW & STANDALONE SOLVERS (Handles candidates, enrolled students & guest previews)
    if (fullPath.startsWith('/learn')) {
      import('../student/LearningView').then(({ LearningView }) => {
        const view = new LearningView(this.container, (p) => this.navigate(p), courseId);
        view.render();
        this.currentComponent = view;
      });
      return;
    }

    if (fullPath.startsWith('/exam-solve')) {
      const testId = searchParams.get('testId') || 'test-comprehensive-demo';
      import('../assessment/solver/ExamSolverView').then(({ ExamSolverView }) => {
        const view = new ExamSolverView(this.container, testId, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });
      return;
    }

    if (fullPath.startsWith('/assignment-solve')) {
      const assignmentId = searchParams.get('assignmentId') || 'assign-svg-canvas';
      const assignedAssignmentId = searchParams.get('assignedAssignmentId') || null;
      import('../assessment/assignment/AssignmentSolverView').then(({ AssignmentSolverView }) => {
        const view = new AssignmentSolverView(this.container, assignmentId, (p) => this.navigate(p), {
          assignedAssignmentId
        });
        view.render();
        this.currentComponent = view;
      });
      return;
    }

    // 4. STRICT AUTHENTICATION GUARD FOR PROTECTED ROUTES
    // If not authenticated or token is expired, clear session and redirect to login screen
    if (!auth.isAuthenticated() || auth.isTokenExpired()) {
      auth.logout(fullPath);
      return;
    }

    // 5. UN-SHELLED PROTECTED ROUTES (Full Screen Studio IDE & Widescreen Player)
    if (fullPath.startsWith('/studio')) {
      if (!auth.isInstructor()) {
        alert('Access Restricted: You do not have instructor or creator permissions to edit courses in Studio.');
        this.navigate('/lms-dashboard');
        return;
      }
      import('../studio/StudioApp').then(({ StudioApp }) => {
        const app = new StudioApp(this.container, {
          initialCourseId: courseId,
          onModeSwitch: () => this.navigate('/lms-dashboard')
        });
        this.currentComponent = app;
        (window as any).studioAppInstance = app;
        setTimeout(() => applyAuthDirectives(this.container), 20);
      });
      return;
    }

    // 5. SHELLED PROTECTED PORTAL ROUTES (Wrapped in Universal AppShell for Logged-In Users)
    const shell = new AppShell(this.container, fullPath, (p) => this.navigate(p));
    shell.render();
    const viewport = shell.getViewportElement();

    if (fullPath.startsWith('/create-course')) {
      if (!auth.hasPermission('course_add') && !auth.hasPermission('course.add') && !auth.hasPermission('course.create')) {
        alert('Access Restricted: You do not have permission to create courses.');
        this.navigate('/lms-dashboard');
        return;
      }
      import('../generator/CourseGeneratorView').then(({ CourseGeneratorView }) => {
        const view = new CourseGeneratorView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/exam-generator')) {
      if (!auth.hasPermission('course_add') && !auth.hasPermission('course.add') && !auth.hasPermission('course.create')) {
        alert('Access Restricted: You do not have permission to manage exams.');
        this.navigate('/lms-dashboard');
        return;
      }
      import('../assessment/generator/ExamGeneratorView').then(({ ExamGeneratorView }) => {
        const view = new ExamGeneratorView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/assignment-generator')) {
      if (!auth.hasPermission('course_add') && !auth.hasPermission('course.add') && !auth.hasPermission('course.create')) {
        alert('Access Restricted: You do not have permission to manage assignments.');
        this.navigate('/lms-dashboard');
        return;
      }
      import('../assessment/generator/AssignmentGeneratorView').then(({ AssignmentGeneratorView }) => {
        const view = new AssignmentGeneratorView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/my-courses')) {
      import('../student/MyCoursesView').then(({ MyCoursesView }) => {
        const view = new MyCoursesView(this.container, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/courses')) {
      if (!auth.hasPermission('course_add') && !auth.hasPermission('course.add') && !auth.hasPermission('course.create') && !auth.isInstructor()) {
        alert('Access Restricted: You do not have permission to view the all courses catalog.');
        this.navigate('/lms-dashboard');
        return;
      }
      import('../student/CoursesView').then(({ CoursesView }) => {
        const view = new CoursesView(this.container, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/my-exams')) {
      import('../assessment/learner/MyExamsView').then(({ MyExamsView }) => {
        const view = new MyExamsView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/my-assignments')) {
      import('../assessment/learner/MyAssignmentsView').then(({ MyAssignmentsView }) => {
        const view = new MyAssignmentsView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
      });

    } else if (fullPath.startsWith('/assignment-checker')) {
      if (!auth.canCheckAssignments()) {
        alert('Access Restricted: You do not have permission to check assignments.');
        this.navigate('/my-assignments');
        return;
      }
      import('../assessment/checker/AssignmentCheckerView').then(({ AssignmentCheckerView }) => {
        const filterAssignmentId = searchParams.get('assignmentId') || undefined;
        const view = new AssignmentCheckerView(viewport, (p) => this.navigate(p), filterAssignmentId);
        view.render();
        this.currentComponent = view;
        setTimeout(() => applyAuthDirectives(this.container), 50);
      });

    } else {
      // Default Protected Route: LMS Dashboard (/lms-dashboard or fallback)
      import('../dashboard/DashboardView').then(({ DashboardView }) => {
        const view = new DashboardView(viewport, (p) => this.navigate(p));
        view.render();
        this.currentComponent = view;
        setTimeout(() => applyAuthDirectives(this.container), 50);
      });
    }
  }
}
